import React, { useState, useEffect } from 'react';
import { linesAPI, tagsAPI } from '../services/api';
import './LinesManager.css';

const LinesManager = () => {
  const [lines, setLines] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [activeLineSection, setActiveLineSection] = useState('user');
  const [userSelectedTag, setUserSelectedTag] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [systemSelectedTag, setSystemSelectedTag] = useState('');
  const [systemSearchTerm, setSystemSearchTerm] = useState('');
  const [formData, setFormData] = useState({ 
    tag_id: '', 
    bulkText: '' 
  });
  const [formErrors, setFormErrors] = useState({});

  const getTagId = (tag) => {
    if (typeof tag === 'string' || typeof tag === 'number') {
      return tag;
    }
    return tag?.id ?? tag?.tag_id ?? tag?.pk ?? null;
  };
  const getLineId = (line) => {
    if (typeof line === 'string' || typeof line === 'number') {
      return line;
    }
    return line?.id ?? line?.line_id ?? line?.pk ?? null;
  };
  const getLineTagId = (line) => {
    if (line?.tag && typeof line.tag === 'object') {
      return getTagId(line.tag);
    }
    if (typeof line?.tag !== 'undefined' && line?.tag !== null) {
      return line.tag;
    }
    return line?.tag_id ?? null;
  };

  const isSystemLine = (line) => {
    if (typeof line.is_system !== 'undefined') {
      return Boolean(line.is_system);
    }

    return Boolean(line.tag?.is_system);
  };

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load tags and lines in parallel
      const [tagsResponse, linesResponse] = await Promise.all([
        tagsAPI.getAll(),
        linesAPI.getAll()
      ]);
      
      setTags(tagsResponse.results || []);
      setLines(linesResponse.results || []);
    } catch (err) {
      setError('Failed to load data. Please try again.');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Parse bulk text input into individual lines
  const parseBulkText = (text) => {
    if (!text.trim()) return [];
    
    // Split by newlines
    const lines = text.split('\n');
    const cleanedLines = [];
    
    for (let line of lines) {
      // Trim whitespace
      line = line.trim();
      
      // Skip empty lines
      if (!line) continue;
      
      // Remove common bullet points and numbering
      // Remove leading dashes, asterisks, numbers, etc.
      let cleanedLine = line;
      
      // Remove leading dash and space
      if (cleanedLine.startsWith('- ')) {
        cleanedLine = cleanedLine.substring(2);
      }
      
      // Remove leading asterisk and space
      if (cleanedLine.startsWith('* ')) {
        cleanedLine = cleanedLine.substring(2);
      }
      
      // Remove leading and trailing quotes
      if (cleanedLine.startsWith('"')) {
        cleanedLine = cleanedLine.substring(1);
      }
      if (cleanedLine.endsWith('"')) {
        cleanedLine = cleanedLine.substring(0, cleanedLine.length - 1);
      }
      
      // Remove leading numbers and dots (e.g., "1. ", "2. ")
      const numberMatch = cleanedLine.match(/^\d+\.\s*/);
      if (numberMatch) {
        cleanedLine = cleanedLine.substring(numberMatch[0].length);
      }
      
      // Remove leading letters and dots (e.g., "a. ", "b. ")
      const letterMatch = cleanedLine.match(/^[a-zA-Z]\.\s*/);
      if (letterMatch) {
        cleanedLine = cleanedLine.substring(letterMatch[0].length);
      }
      
      // Trim again after cleaning
      cleanedLine = cleanedLine.trim();
      
      // Only add if the line has content after cleaning
      if (cleanedLine && cleanedLine.length >= 3) {
        cleanedLines.push(cleanedLine);
      }
    }
    
    return cleanedLines;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };


  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.tag_id) {
      newErrors.tag_id = 'Please select a tag';
    } else if (!editingLine) {
      const selectedTagData = tags.find(tag => String(getTagId(tag)) === String(formData.tag_id));
      if (selectedTagData?.is_system) {
        newErrors.tag_id = 'System tags are read-only. Please select a user tag.';
      }
    }
    
    const parsedLines = parseBulkText(formData.bulkText);
    if (parsedLines.length === 0) {
      newErrors.bulkText = 'Please enter at least one line';
    } else if (parsedLines.length > 50) {
      newErrors.bulkText = 'Maximum 50 lines allowed at once';
    }
    
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateLines = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setError(null);
      const parsedLines = parseBulkText(formData.bulkText);
      const response = await linesAPI.create(formData.tag_id, parsedLines);
      
      // Add new lines to the list
      setLines(prev => [...response.created_lines, ...prev]);
      
      // Reset form
      setFormData({ tag_id: '', bulkText: '' });
      setShowCreateForm(false);
    } catch (err) {
      if (err.lines) {
        setFormErrors({ bulkText: err.lines[0] });
      } else {
        setError('Failed to create lines. Please try again.');
      }
      console.error('Error creating lines:', err);
    }
  };

  const handleEditLine = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const editingLineId = getLineId(editingLine);
    if (!editingLineId) {
      setError('Cannot update line: missing line ID.');
      return;
    }

    try {
      setError(null);
      const parsedLines = parseBulkText(formData.bulkText);
      if (parsedLines.length !== 1) {
        setFormErrors({ bulkText: 'Please enter exactly one line for editing' });
        return;
      }
      
      const updatedLine = await linesAPI.update(editingLineId, parsedLines[0]);
      setLines(prev => prev.map(line => 
        String(getLineId(line)) === String(editingLineId) ? updatedLine : line
      ));
      setFormData({ tag_id: '', bulkText: '' });
      setEditingLine(null);
    } catch (err) {
      if (err.content) {
        setFormErrors({ bulkText: err.content[0] });
      } else {
        setError('Failed to update line. Please try again.');
      }
      console.error('Error updating line:', err);
    }
  };

  const handleDeleteLine = async (line) => {
    const lineId = getLineId(line);
    if (!lineId) {
      setError('Cannot delete line: missing line ID.');
      return;
    }

    if (isSystemLine(line)) {
      setError('System lines are read-only and cannot be deleted.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this line?')) {
      return;
    }

    try {
      setError(null);
      await linesAPI.delete(lineId);
      setLines(prev => prev.filter(existingLine => String(getLineId(existingLine)) !== String(lineId)));
    } catch (err) {
      setError('Failed to delete line. Please try again.');
      console.error('Error deleting line:', err);
    }
  };

  const startEdit = (line) => {
    const lineId = getLineId(line);
    if (!lineId) {
      setError('Cannot edit line: missing line ID.');
      return;
    }

    if (isSystemLine(line)) {
      setError('System lines are read-only and cannot be edited.');
      return;
    }

    const lineTagId = getLineTagId(line);

    setEditingLine({ ...line, id: lineId });
    setActiveLineSection('user');
    setFormData({ 
      tag_id: lineTagId || '',
      bulkText: line.content 
    });
    setShowCreateForm(false);
    setFormErrors({});
  };

  const cancelEdit = () => {
    setEditingLine(null);
    setFormData({ tag_id: '', bulkText: '' });
    setFormErrors({});
  };

  const cancelCreate = () => {
    setShowCreateForm(false);
    setFormData({ tag_id: '', bulkText: '' });
    setFormErrors({});
  };

  const editableTags = tags.filter(tag => !tag.is_system);
  const systemTags = tags.filter(tag => Boolean(tag.is_system));
  const hasUserTags = editableTags.length > 0;

  const userSourceLines = lines.filter(line => !isSystemLine(line));
  const systemSourceLines = lines.filter(line => isSystemLine(line));

  const userLines = userSourceLines.filter(line => {
    const matchesSearch = line.content.toLowerCase().includes(userSearchTerm.toLowerCase());
    const lineTagId = getLineTagId(line);
    const matchesTag = !userSelectedTag || String(lineTagId) === userSelectedTag;
    return matchesSearch && matchesTag;
  });

  const systemLines = systemSourceLines.filter(line => {
    const matchesSearch = line.content.toLowerCase().includes(systemSearchTerm.toLowerCase());
    const lineTagId = getLineTagId(line);
    const matchesTag = !systemSelectedTag || String(lineTagId) === systemSelectedTag;
    return matchesSearch && matchesTag;
  });

  const renderLinesSection = (sectionLines, isSystemSection, hasActiveFilters) => (
    <section className="lines-section">
      {sectionLines.length === 0 ? (
        <div className="section-empty">
          <p>
            {hasActiveFilters
              ? `No ${isSystemSection ? 'system' : 'user'} lines match your filters.`
              : `No ${isSystemSection ? 'system' : 'user'} lines available.`
            }
          </p>
        </div>
      ) : (
        <div className="lines-list-container">
          {sectionLines.map(line => (
            <div key={getLineId(line) ?? `line-${line.content}`} className="line-item">
              <div className="line-info">
                <div className="line-tag">
                  {line.tag?.name || 'Unknown Tag'}
                </div>
                <p className="line-content">{line.content}</p>
                <div className="line-meta">
                  {line.performance_score && (
                    <span className="performance-score">
                      Score: {line.performance_score.toFixed(1)}/10
                    </span>
                  )}
                  {line.last_practiced && (
                    <span className="last-practiced">
                      Last practiced: {new Date(line.last_practiced).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>

              {isSystemSection ? (
                <div className="readonly-badge">Read-only</div>
              ) : (
                <div className="line-actions">
                  <button
                    className="btn btn-small btn-secondary"
                    onClick={() => startEdit(line)}
                    disabled={showCreateForm}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-small btn-danger"
                    onClick={() => handleDeleteLine(line)}
                    disabled={showCreateForm}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );

  if (loading) {
    return (
      <div className="lines-manager">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading lines...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="lines-manager">
      <div className="lines-header">
        <h2>Lines Management</h2>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="lines-subtabs">
        <div className="lines-subtabs-header" role="tablist" aria-label="Line sections">
          <button
            type="button"
            role="tab"
            className={`subtab-button ${activeLineSection === 'user' ? 'active' : ''}`}
            aria-selected={activeLineSection === 'user'}
            onClick={() => setActiveLineSection('user')}
          >
            User Lines <span className="section-count">{userLines.length}</span>
          </button>
          <button
            type="button"
            role="tab"
            className={`subtab-button ${activeLineSection === 'system' ? 'active' : ''}`}
            aria-selected={activeLineSection === 'system'}
            onClick={() => setActiveLineSection('system')}
          >
            System Lines <span className="section-count">{systemLines.length}</span>
          </button>
        </div>

        {activeLineSection === 'user' && (
          <>
            <div className="lines-filters">
              <div className="filter-group">
                <label htmlFor="user-tag-filter">Filter by Tag:</label>
                <select
                  id="user-tag-filter"
                  value={userSelectedTag}
                  onChange={(e) => setUserSelectedTag(e.target.value)}
                >
                  <option value="">All User Tags</option>
                  {editableTags.map(tag => (
                    <option key={getTagId(tag) ?? `user-tag-${tag.name}`} value={getTagId(tag) ?? ''}>
                      {tag.name} ({tag.line_count} lines)
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="user-search">Search User Lines:</label>
                <input
                  type="text"
                  id="user-search"
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  placeholder="Search user line content..."
                />
              </div>
            </div>

            <div className="section-actions">
              <button
                className="btn btn-primary"
                onClick={() => setShowCreateForm(true)}
                disabled={editingLine || !hasUserTags}
                title={!hasUserTags ? 'Create a user tag to add new lines.' : undefined}
              >
                + Add New Lines
              </button>
            </div>

            {(showCreateForm || editingLine) && (
              <div className="lines-form">
                <h3>{editingLine ? 'Edit Line' : 'Add New Lines'}</h3>
                <form onSubmit={editingLine ? handleEditLine : handleCreateLines}>
                  <div className="form-group">
                    <label htmlFor="tag_id">Tag</label>
                    <select
                      id="tag_id"
                      name="tag_id"
                      value={formData.tag_id}
                      onChange={handleInputChange}
                      className={formErrors.tag_id ? 'error' : ''}
                      disabled={editingLine}
                    >
                      <option value="">Select a tag</option>
                      {editableTags.map(tag => (
                        <option key={getTagId(tag) ?? `form-tag-${tag.name}`} value={getTagId(tag) ?? ''}>
                          {tag.name}
                        </option>
                      ))}
                    </select>
                    {formErrors.tag_id && (
                      <span className="error-text">{formErrors.tag_id}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="bulkText">
                      {editingLine ? 'Line Content' : 'Lines (one per line)'}
                    </label>
                    <textarea
                      id="bulkText"
                      name="bulkText"
                      value={formData.bulkText}
                      onChange={handleInputChange}
                      placeholder={editingLine
                        ? 'Enter the line content...'
                        : `Paste your lines here, one per line. Examples:
- You are a certified badass
* I'm totally adopting you
"Has anyone told you... you have a really healing vibe"
1. You're like the girl version of me right now
2. You seem really grounded`}
                      rows={editingLine ? 3 : 8}
                      className={formErrors.bulkText ? 'error' : ''}
                    />
                    {!editingLine && (
                      <div className="bulk-input-help">
                        <p><strong>Tips:</strong></p>
                        <ul>
                          <li>Each line will be automatically cleaned (bullets, numbers, quotes removed)</li>
                          <li>Empty lines will be ignored</li>
                          <li>Lines must be at least 3 characters long</li>
                          <li>Maximum 50 lines at once</li>
                        </ul>
                      </div>
                    )}
                    {formErrors.bulkText && (
                      <span className="error-text">{formErrors.bulkText}</span>
                    )}
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary">
                      {editingLine ? 'Update Line' : 'Create Lines'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={editingLine ? cancelEdit : cancelCreate}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="lines-list">
              {renderLinesSection(userLines, false, Boolean(userSearchTerm || userSelectedTag))}
            </div>
          </>
        )}

        {activeLineSection === 'system' && (
          <>
            <div className="lines-filters">
              <div className="filter-group">
                <label htmlFor="system-tag-filter">Filter by Tag:</label>
                <select
                  id="system-tag-filter"
                  value={systemSelectedTag}
                  onChange={(e) => setSystemSelectedTag(e.target.value)}
                >
                  <option value="">All System Tags</option>
                  {systemTags.map(tag => (
                    <option key={getTagId(tag) ?? `system-tag-${tag.name}`} value={getTagId(tag) ?? ''}>
                      {tag.name} ({tag.line_count} lines)
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="system-search">Search System Lines:</label>
                <input
                  type="text"
                  id="system-search"
                  value={systemSearchTerm}
                  onChange={(e) => setSystemSearchTerm(e.target.value)}
                  placeholder="Search system line content..."
                />
              </div>
            </div>

            <div className="lines-list">
              {renderLinesSection(systemLines, true, Boolean(systemSearchTerm || systemSelectedTag))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LinesManager;
